import React from 'react';

/**
 * Utility functions for expanding categories in tables
 * 
 * These functions help automatically expand parent items (categories) that have children
 * when entering edit mode, after auto-save recovery, or after drag and drop operations.
 */

/**
 * Predicate function type to identify if an item is a category that should be expanded
 * @param item - The data item to check
 * @returns true if the item is a category that should be expanded
 */
export type TCategoryPredicate = (item: any) => boolean;

/**
 * Default predicate: expands items that have children array
 */
export const defaultCategoryPredicate: TCategoryPredicate = (item: any) => {
  return Boolean(item.children && Array.isArray(item.children) && item.children.length > 0);
};

/**
 * Predicate for items with isCategory flag
 */
export const isCategoryPredicate: TCategoryPredicate = (item: any) => {
  return item.isCategory === true && Boolean(item.children && Array.isArray(item.children) && item.children.length > 0);
};

/**
 * Collects all IDs of items that match the category predicate
 * Recursively traverses the data structure to find all categories with children
 * 
 * @param data - Array of data items (may contain nested children)
 * @param predicate - Function to identify if an item is a category (default: checks for children)
 * @returns Array of category IDs as strings
 */
export const collectCategoryIds = (
  data: any[],
  predicate: TCategoryPredicate = isCategoryPredicate
): string[] => {
  const categoryIds = new Set<string>();
  
  const collectRecursive = (items: any[]): void => {
    items.forEach((item: any) => {
      if (predicate(item)) {
        categoryIds.add(String(item.id));
        // Recursively check children
        if (item.children && Array.isArray(item.children)) {
          collectRecursive(item.children);
        }
      }
    });
  };
  
  collectRecursive(data);
  return Array.from(categoryIds);
};

/**
 * Expands all categories in the data that match the predicate
 * This is a helper function that can be used with setExpandedKeys
 * 
 * @param data - Array of data items (may contain nested children)
 * @param setExpandedKeys - State setter function for expandedKeys
 * @param predicate - Function to identify if an item is a category (default: isCategoryPredicate)
 * @param expandedKeysRef - Optional ref to sync expandedKeys (for tracking purposes)
 */
export const expandAllCategories = (
  data: any[],
  setExpandedKeys: (keys: string[]) => void,
  predicate: TCategoryPredicate = isCategoryPredicate,
  expandedKeysRef?: React.MutableRefObject<string[]>
): void => {
  if (!data || data.length === 0) {
    return;
  }
  
  const categoryIds = collectCategoryIds(data, predicate);
  
  if (categoryIds.length > 0) {
    console.log('📂 [TABLE EXPAND] Expanding all categories with children:', categoryIds);
    setExpandedKeys(categoryIds);
    if (expandedKeysRef) {
      expandedKeysRef.current = categoryIds;
    }
  }
};

/**
 * Helper function to expand categories after drag and drop operations
 * Gets current data from state or ref, then expands categories
 * 
 * @param stateData - Current data from state (e.g., mktCostProjectsData, projectsData)
 * @param tableRef - Ref to TableContent component
 * @param expandFn - Function to expand categories (created with expandAllCategories)
 * @param delay - Optional delay in milliseconds (default: 0)
 */
export const expandCategoriesAfterDrag = (
  stateData: any[],
  tableRef: React.RefObject<any>,
  expandFn: (data: any[]) => void,
  delay: number = 0
): void => {
  setTimeout(() => {
    const currentData = stateData.length > 0 ? stateData : (tableRef.current?.getTableData?.() || []);
    if (currentData.length > 0) {
      expandFn(currentData);
    }
  }, delay);
};

